import React, { useEffect, useMemo, useState } from 'react';
import api from '../lib/api';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { StatusBadge } from '../components/StatusBadge';
import { formatIDR, formatIDRCompact, formatDateID } from '../lib/format';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, BarChart, Bar, PieChart, Pie, Cell,
} from 'recharts';
import {
  Filter, ChevronDown, ChevronUp, Sparkles, AlertTriangle, TrendingUp, Wallet,
  FileCheck2, FileText, Receipt, Clock, Phone, MessageSquare, ArrowRight,
  Layers, LayoutGrid, Eye, Settings2, Info,
} from 'lucide-react';

// ===========================================================================
// CONFIG
// ===========================================================================
const PERIOD_PRESETS = [
  { value: 'this_month', label: 'Bulan Ini' },
  { value: 'last_month', label: 'Bulan Lalu' },
  { value: 'last_3_months', label: '3 Bulan Terakhir' },
  { value: 'this_year', label: 'Tahun Ini' },
  { value: 'custom', label: 'Custom Range' },
];

// "Jenis Laporan" – advanced override for power users.
// Default = per-metric fixed basis (recommended).
const REPORT_VIEW_OPTIONS = [
  { value: 'default', label: 'Standar (per metrik)', hint: 'Setiap KPI memakai tanggal logisnya sendiri (penawaran/invoice/pembayaran). Direkomendasikan.' },
  { value: 'event_date', label: 'Event di Periode Ini', hint: 'Semua KPI difilter berdasarkan tanggal event.' },
  { value: 'invoice_date', label: 'Invoice Dibuat di Periode Ini', hint: 'Penawaran berdasarkan tanggal penawaran, invoice berdasarkan tanggal invoice.' },
  { value: 'payment_date', label: 'Pembayaran Masuk di Periode Ini', hint: 'Mode lama berbasis pembayaran. Gunakan hanya bila perlu.' },
];

const PIE_COLORS = ['#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#06b6d4', '#a855f7', '#22c55e'];

// Dark-theme readability constants
const CHART_AXIS_COLOR = '#94A3B8';        // slate-400
const CHART_GRID_COLOR = 'rgba(148,163,184,0.14)';
const TOOLTIP_CONTENT_STYLE = {
  background: '#0B1020',
  border: '1px solid #334155',
  borderRadius: 8,
  boxShadow: '0 10px 25px -10px rgba(0,0,0,0.6)',
  padding: '8px 10px',
  fontSize: 12,
  color: '#EAF0FF',
};
const TOOLTIP_LABEL_STYLE = { color: '#CBD5E1', fontWeight: 600, fontSize: 11, marginBottom: 2 };
const TOOLTIP_ITEM_STYLE = { color: '#EAF0FF', fontSize: 12 };
const LEGEND_WRAPPER_STYLE = { fontSize: 12, color: '#CBD5E1' };

const monthLabel = (key) => {
  if (!key) return '';
  const [y, m] = key.split('-');
  const names = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'];
  return `${names[parseInt(m, 10) - 1] || m} ${String(y).slice(2)}`;
};

// ===========================================================================
// MAIN
// ===========================================================================
export default function Dashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('ringkas'); // 'ringkas' | 'detail'
  const [filters, setFilters] = useState({
    period: 'this_month',
    date_from: '',
    date_to: '',
    date_basis: 'default',
  });
  const [appliedFilters, setAppliedFilters] = useState(filters);

  const load = async (f) => {
    setLoading(true);
    try {
      const params = { period: f.period, date_basis: f.date_basis };
      if (f.period === 'custom') {
        if (f.date_from) params.date_from = f.date_from;
        if (f.date_to) params.date_to = f.date_to;
      }
      const { data } = await api.get('/dashboard/owner-summary', { params });
      setData(data);
    } catch (e) {
      // Surface failures so dashboard regressions don't go unnoticed.
      console.error('[Dashboard] owner-summary load failed', e);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(appliedFilters); }, [appliedFilters]); // eslint-disable-line react-hooks/exhaustive-deps

  const apply = () => setAppliedFilters({ ...filters });
  const reset = () => {
    const def = { period: 'this_month', date_from: '', date_to: '', date_basis: 'default' };
    setFilters(def); setAppliedFilters(def);
  };

  return (
    <div className="space-y-4 sm:space-y-5" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-slate-100">Dashboard</h1>
          <p className="text-sm text-slate-400">Ringkasan performa bisnis & prioritas tindakan</p>
        </div>
        <div className="flex items-center gap-1 rounded-full border border-border bg-card/60 p-0.5 self-start sm:self-auto" data-testid="dashboard-view-toggle">
          <ViewToggleButton active={view === 'ringkas'} onClick={() => setView('ringkas')} icon={<Layers className="h-3.5 w-3.5" />} testid="dashboard-view-ringkas">Ringkas</ViewToggleButton>
          <ViewToggleButton active={view === 'detail'} onClick={() => setView('detail')} icon={<LayoutGrid className="h-3.5 w-3.5" />} testid="dashboard-view-detail">Detail</ViewToggleButton>
        </div>
      </div>

      {/* Global Filter */}
      <DashboardFilter filters={filters} setFilters={setFilters} onApply={apply} onReset={reset} />

      {/* Insight */}
      <InsightCard data={data} loading={loading} />

      {/* KPI Grid */}
      <KpiGrid data={data} loading={loading} />

      {/* Charts */}
      <ChartsRow data={data} loading={loading} />

      {/* Action: Collection Priority (always in Ringkas + Detail) */}
      <CollectionPrioritySection
        items={data?.action_lists?.collection_priority || []}
        loading={loading}
        navigate={navigate}
      />

      {view === 'detail' && (
        <>
          <DueSoonSection items={data?.action_lists?.due_soon || []} loading={loading} navigate={navigate} />
          <QuotationFollowupSection items={data?.action_lists?.quotations_followup || []} loading={loading} navigate={navigate} />
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
            <div className="lg:col-span-2"><TaxSummaryCard tax={data?.detail?.tax_summary} loading={loading} /></div>
            <div className="lg:col-span-3"><TopClientsTable clients={data?.detail?.top_clients || []} loading={loading} /></div>
          </div>
        </>
      )}
    </div>
  );
}

// ===========================================================================
// COMPONENTS
// ===========================================================================
const ViewToggleButton = ({ active, onClick, icon, children, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={[
      'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition-colors',
      active ? 'bg-primary text-primary-foreground' : 'text-slate-300 hover:text-white',
    ].join(' ')}
  >
    {icon}<span>{children}</span>
  </button>
);

// ---------------------------------------------------------------------------
const DashboardFilter = ({ filters, setFilters, onApply, onReset }) => {
  const [open, setOpen] = useState(true);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const isCustom = filters.period === 'custom';
  const reportViewLabel =
    REPORT_VIEW_OPTIONS.find(o => o.value === filters.date_basis)?.label || 'Standar (per metrik)';
  const isAdvancedActive = filters.date_basis !== 'default';

  return (
    <Card className="bg-card border-border" data-testid="dashboard-filter-card">
      <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
        <CardTitle className="text-sm font-heading flex items-center gap-2 text-slate-100">
          <Filter className="h-4 w-4 text-primary" /> Filter Dashboard
        </CardTitle>
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          className="text-xs text-slate-300 hover:text-white inline-flex items-center gap-1"
          data-testid="dashboard-filter-toggle"
        >
          {open ? <>Sembunyikan <ChevronUp className="h-3 w-3" /></> : <>Tampilkan <ChevronDown className="h-3 w-3" /></>}
        </button>
      </CardHeader>
      {open && (
        <CardContent className="space-y-3">
          {/* Main filter row — simple */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label className="text-[11px] text-slate-300">Periode</Label>
              <Select value={filters.period} onValueChange={(v) => setFilters(p => ({ ...p, period: v }))}>
                <SelectTrigger className="h-11 sm:h-10 text-slate-100" data-testid="dashboard-filter-period">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PERIOD_PRESETS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11px] text-slate-300">Dari Tanggal</Label>
              <Input
                type="date"
                value={filters.date_from}
                onChange={(e) => setFilters(p => ({ ...p, date_from: e.target.value }))}
                disabled={!isCustom}
                className="h-11 sm:h-10 date-input-mobile text-slate-100 disabled:opacity-60"
                data-testid="dashboard-filter-from"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11px] text-slate-300">Sampai Tanggal</Label>
              <Input
                type="date"
                value={filters.date_to}
                onChange={(e) => setFilters(p => ({ ...p, date_to: e.target.value }))}
                disabled={!isCustom}
                className="h-11 sm:h-10 date-input-mobile text-slate-100 disabled:opacity-60"
                data-testid="dashboard-filter-to"
              />
            </div>
          </div>

          {/* Advanced filter toggle */}
          <div className="pt-1">
            <button
              type="button"
              onClick={() => setAdvancedOpen(o => !o)}
              className="inline-flex items-center gap-1.5 text-[11px] font-medium text-slate-300 hover:text-white"
              data-testid="dashboard-advanced-toggle"
            >
              <Settings2 className="h-3.5 w-3.5" />
              Filter Lanjutan
              {isAdvancedActive && (
                <span
                  className="ml-1 px-1.5 py-0.5 rounded-full text-[9px] bg-primary/20 text-primary border border-primary/40"
                  title={reportViewLabel}
                >
                  {reportViewLabel}
                </span>
              )}
              {advancedOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            </button>
            {advancedOpen && (
              <div className="mt-2 rounded-lg border border-border bg-card/40 p-3 space-y-2">
                <div className="space-y-1.5">
                  <Label className="text-[11px] text-slate-300">Jenis Laporan</Label>
                  <Select value={filters.date_basis} onValueChange={(v) => setFilters(p => ({ ...p, date_basis: v }))}>
                    <SelectTrigger className="h-10 text-slate-100" data-testid="dashboard-filter-basis">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {REPORT_VIEW_OPTIONS.map(o => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <p className="text-[11px] leading-relaxed text-slate-400 flex gap-1.5">
                  <Info className="h-3.5 w-3.5 mt-0.5 shrink-0 text-slate-500" />
                  <span>
                    <strong className="text-slate-300">Jenis Laporan</strong> menentukan tanggal mana yang dipakai untuk memfilter data.
                    Gunakan filter ini hanya jika ingin melihat laporan dari sudut pandang tertentu.
                  </span>
                </p>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="ghost" size="sm" onClick={onReset} data-testid="dashboard-filter-reset" className="text-slate-200 hover:text-white">
              Reset
            </Button>
            <Button size="sm" onClick={onApply} data-testid="dashboard-filter-apply">
              Terapkan Filter
            </Button>
          </div>
        </CardContent>
      )}
    </Card>
  );
};

// ---------------------------------------------------------------------------
const InsightCard = ({ data, loading }) => {
  const i = data?.insight;
  const text = useMemo(() => {
    if (!i) return '';
    const parts = [];
    parts.push(`Cash masuk periode ini ${formatIDRCompact(i.cash_received)}.`);
    parts.push(`Outstanding saat ini ${formatIDRCompact(i.current_outstanding)}.`);
    if (i.current_overdue > 0) {
      parts.push(`Total overdue ${formatIDRCompact(i.current_overdue)} dari ${i.overdue_clients_count} client.`);
    } else {
      parts.push(`Tidak ada overdue saat ini.`);
    }
    if (i.due_soon_count > 0) parts.push(`Ada ${i.due_soon_count} invoice jatuh tempo dalam 7 hari.`);
    parts.push(`Collection rate periode ini ${i.collection_rate.toFixed(1)}%.`);
    if (i.pending_quotation_value > 0) parts.push(`Pipeline penawaran terkirim: ${formatIDRCompact(i.pending_quotation_value)}.`);
    return parts.join(' ');
  }, [i]);

  return (
    <Card className="bg-gradient-to-br from-primary/15 via-card to-card border-primary/30" data-testid="dashboard-insight-card">
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-start gap-3">
          <div className="rounded-lg bg-primary/20 p-2 shrink-0">
            <Sparkles className="h-5 w-5 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] uppercase tracking-wide text-primary font-medium mb-1">Insight Periode Ini</div>
            {loading
              ? <Skeleton className="h-4 w-full max-w-2xl" />
              : <p className="text-sm sm:text-base text-slate-100 leading-relaxed" data-testid="dashboard-insight-text">{text}</p>
            }
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------------------
const KpiGrid = ({ data, loading }) => {
  const k = data?.kpis;
  const cards = [
    {
      label: 'Total Penawaran', icon: FileText, value: k?.total_quotation,
      sub: `${k?.total_quotation_count || 0} penawaran`,
      basis: 'berdasarkan tanggal penawaran',
      accent: '', testid: 'kpi-total-quotation',
    },
    {
      label: 'Penawaran Disetujui', icon: FileCheck2, value: k?.total_approved_quotation,
      sub: `${k?.approved_quotation_count || 0} disetujui`,
      basis: 'berdasarkan tanggal penawaran',
      accent: 'success', testid: 'kpi-approved-quotation',
    },
    {
      label: 'Total Invoice', icon: Receipt, value: k?.total_invoice,
      sub: `${k?.total_invoice_count || 0} invoice`,
      basis: 'berdasarkan tanggal invoice',
      accent: '', testid: 'kpi-total-invoice',
    },
    {
      label: 'Total Terbayar', icon: Wallet, value: k?.total_paid,
      sub: `${k?.total_payment_count || 0} pembayaran`,
      basis: 'berdasarkan tanggal pembayaran',
      accent: 'success', testid: 'kpi-total-paid',
    },
    {
      label: 'Outstanding Saat Ini', icon: TrendingUp, value: k?.current_outstanding,
      sub: `${k?.unpaid_invoice_count || 0} invoice belum lunas`,
      basis: 'kondisi saat ini',
      accent: 'warn', testid: 'kpi-current-outstanding', tooltip: 'Saat Ini',
    },
    {
      label: 'Overdue Saat Ini', icon: AlertTriangle, value: k?.current_overdue,
      sub: `${k?.overdue_invoice_count || 0} invoice • ${k?.overdue_clients_count || 0} client`,
      basis: 'kondisi saat ini',
      accent: 'danger', testid: 'kpi-current-overdue', tooltip: 'Saat Ini',
    },
    {
      label: 'Jatuh Tempo 7 Hari', icon: Clock, value: k?.due_soon_amount,
      sub: `${k?.due_soon_count || 0} invoice`,
      basis: '7 hari ke depan',
      accent: 'warn', testid: 'kpi-due-soon',
    },
    {
      label: 'Collection Rate', icon: TrendingUp, value: null,
      customValue: k ? `${(k.collection_rate || 0).toFixed(1)}%` : null,
      sub: k ? `${formatIDRCompact(k.total_paid)} dari ${formatIDRCompact(k.total_invoice)} tertagih` : '',
      basis: 'terbayar ÷ invoice di periode ini',
      accent: k?.collection_rate >= 80 ? 'success' : k?.collection_rate >= 50 ? 'warn' : 'danger',
      testid: 'kpi-collection-rate',
    },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="dashboard-kpi-grid">
      {cards.map((c) => <KpiCardItem key={c.label} {...c} loading={loading} />)}
    </div>
  );
};

const KpiCardItem = ({ label, icon: Icon, value, customValue, sub, basis, accent, testid, tooltip, loading }) => {
  const accentMap = {
    success: 'border-emerald-400/30 bg-emerald-400/5 text-emerald-300',
    warn: 'border-amber-400/30 bg-amber-400/5 text-amber-300',
    danger: 'border-red-400/30 bg-red-400/5 text-red-300',
    '': 'border-border bg-card/60 text-primary',
  };
  const tooltipMap = {
    success: 'bg-emerald-400/20 border-emerald-400/40 text-emerald-200',
    warn: 'bg-amber-400/20 border-amber-400/40 text-amber-200',
    danger: 'bg-red-400/20 border-red-400/40 text-red-200',
    '': 'bg-primary/20 border-primary/40 text-primary',
  };
  return (
    <Card className={`border ${accentMap[accent] || accentMap['']}`} data-testid={testid}>
      <CardContent className="p-3 sm:p-4">
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="text-[11px] text-slate-200 font-medium leading-tight">{label}</div>
          <Icon className="h-4 w-4 shrink-0 opacity-90" />
        </div>
        {loading ? (
          <Skeleton className="h-7 w-24" />
        ) : (
          <div className="font-mono-num text-xl sm:text-2xl font-semibold tracking-tight text-slate-50">
            {customValue ?? formatIDRCompact(value || 0)}
          </div>
        )}
        <div className="flex items-center justify-between gap-1 mt-1">
          <div className="text-[10px] text-slate-300 truncate">{sub}</div>
          {tooltip && (
            <span
              className={`text-[9px] px-1.5 py-0.5 rounded-full border shrink-0 font-medium ${tooltipMap[accent] || tooltipMap['']}`}
            >
              {tooltip}
            </span>
          )}
        </div>
        {basis && (
          <div className="text-[10px] text-slate-400 italic mt-0.5 truncate" title={basis}>{basis}</div>
        )}
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------------------
const ChartsRow = ({ data, loading }) => {
  const trend = data?.charts?.invoice_vs_payment || [];
  const aging = data?.charts?.receivable_aging || [];
  const status = data?.charts?.status_breakdown || [];
  return (
    <>
      <Card className="bg-card border-border" data-testid="chart-trend-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-heading text-slate-100">Tren Invoice vs Pembayaran</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? <Skeleton className="h-[260px] w-full" /> : trend.length === 0 ? (
            <div className="h-[200px] flex items-center justify-center text-sm text-slate-400">Belum ada data pada periode ini.</div>
          ) : (
            <div className="w-full h-[260px]">
              <ResponsiveContainer>
                <LineChart data={trend} margin={{ left: 0, right: 6, top: 6, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID_COLOR} />
                  <XAxis
                    dataKey="month"
                    tickFormatter={monthLabel}
                    tick={{ fontSize: 11, fill: CHART_AXIS_COLOR }}
                    axisLine={{ stroke: '#334155' }}
                    tickLine={{ stroke: '#334155' }}
                  />
                  <YAxis
                    tick={{ fontSize: 10, fill: CHART_AXIS_COLOR }}
                    tickFormatter={(v) => formatIDRCompact(v).replace('Rp ', '')}
                    axisLine={{ stroke: '#334155' }}
                    tickLine={{ stroke: '#334155' }}
                  />
                  <Tooltip
                    contentStyle={TOOLTIP_CONTENT_STYLE}
                    labelStyle={TOOLTIP_LABEL_STYLE}
                    itemStyle={TOOLTIP_ITEM_STYLE}
                    cursor={{ stroke: 'rgba(148,163,184,0.25)', strokeWidth: 1 }}
                    labelFormatter={(l) => monthLabel(l)}
                    formatter={(v, n) => [formatIDR(v), n === 'invoice' ? 'Invoice Diterbitkan' : 'Pembayaran Masuk']}
                  />
                  <Legend
                    wrapperStyle={LEGEND_WRAPPER_STYLE}
                    formatter={(v) => <span style={{ color: '#CBD5E1' }}>{v === 'invoice' ? 'Invoice Diterbitkan' : 'Pembayaran Masuk'}</span>}
                  />
                  <Line type="monotone" dataKey="invoice" stroke="#8b5cf6" strokeWidth={2} dot={{ r: 3, fill: '#8b5cf6' }} activeDot={{ r: 5 }} />
                  <Line type="monotone" dataKey="payment" stroke="#10b981" strokeWidth={2} dot={{ r: 3, fill: '#10b981' }} activeDot={{ r: 5 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="bg-card border-border" data-testid="chart-aging-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-heading text-slate-100">Aging Piutang</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? <Skeleton className="h-[220px] w-full" /> : (
              <div className="w-full h-[220px]">
                <ResponsiveContainer>
                  <BarChart data={aging} margin={{ left: 0, right: 6, top: 6, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID_COLOR} />
                    <XAxis
                      dataKey="bucket"
                      tick={{ fontSize: 10, fill: CHART_AXIS_COLOR }}
                      axisLine={{ stroke: '#334155' }}
                      tickLine={{ stroke: '#334155' }}
                    />
                    <YAxis
                      tick={{ fontSize: 10, fill: CHART_AXIS_COLOR }}
                      tickFormatter={(v) => formatIDRCompact(v).replace('Rp ', '')}
                      axisLine={{ stroke: '#334155' }}
                      tickLine={{ stroke: '#334155' }}
                    />
                    <Tooltip
                      contentStyle={TOOLTIP_CONTENT_STYLE}
                      labelStyle={TOOLTIP_LABEL_STYLE}
                      itemStyle={TOOLTIP_ITEM_STYLE}
                      cursor={{ fill: 'rgba(148,163,184,0.08)' }}
                      formatter={(v) => [formatIDR(v), 'Outstanding']}
                    />
                    <Bar dataKey="amount" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="bg-card border-border" data-testid="chart-status-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-heading text-slate-100">Status Invoice / Piutang</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? <Skeleton className="h-[220px] w-full" /> : status.length === 0 ? (
              <div className="h-[200px] flex items-center justify-center text-sm text-slate-400">Belum ada data pada periode ini.</div>
            ) : (
              <div className="w-full h-[220px]">
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={status} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={45} outerRadius={75} paddingAngle={2} stroke="#0B1020" strokeWidth={2}>
                      {status.map((entry, idx) => <Cell key={entry.name} fill={PIE_COLORS[idx % PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip
                      contentStyle={TOOLTIP_CONTENT_STYLE}
                      labelStyle={TOOLTIP_LABEL_STYLE}
                      itemStyle={TOOLTIP_ITEM_STYLE}
                      formatter={(v, n) => [`${v} invoice`, n]}
                    />
                    <Legend
                      layout="vertical"
                      align="right"
                      verticalAlign="middle"
                      wrapperStyle={LEGEND_WRAPPER_STYLE}
                      formatter={(v) => <span style={{ color: '#CBD5E1' }}>{v}</span>}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
};

// ---------------------------------------------------------------------------
const SectionEmpty = ({ children }) => (
  <div className="text-center py-6 text-sm text-slate-400">{children}</div>
);

const TH = ({ children, align = 'left' }) => (
  <th className={`text-${align} px-2 py-2 text-[11px] font-medium uppercase tracking-wide text-slate-300`}>{children}</th>
);

const CollectionPrioritySection = ({ items, loading, navigate }) => (
  <Card className="bg-card border-border" data-testid="collection-priority-card">
    <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
      <CardTitle className="text-sm font-heading flex items-center gap-2 text-slate-100">
        <AlertTriangle className="h-4 w-4 text-amber-300" /> Prioritas Penagihan
      </CardTitle>
      <button type="button" onClick={() => navigate('/receivables')} className="text-xs text-primary hover:underline inline-flex items-center gap-0.5">
        Semua <ArrowRight className="h-3 w-3" />
      </button>
    </CardHeader>
    <CardContent className="p-0 sm:p-4">
      {loading ? <Skeleton className="h-32 mx-4 sm:mx-0" /> :
       items.length === 0 ? <SectionEmpty>Tidak ada invoice yang perlu ditagih saat ini.</SectionEmpty> : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border">
                <tr>
                  <TH>Client</TH>
                  <TH>Invoice</TH>
                  <TH>Event</TH>
                  <TH>Venue</TH>
                  <TH align="right">Outstanding</TH>
                  <TH>Jatuh Tempo</TH>
                  <TH align="right">Aging</TH>
                  <TH>Last FU</TH>
                  <TH>Status</TH>
                  <TH align="right">Aksi</TH>
                </tr>
              </thead>
              <tbody>
                {items.map(i => (
                  <tr key={i.id} className="border-b border-border/40 hover:bg-white/[0.03]" data-testid={`collection-row-${i.id}`}>
                    <td className="px-2 py-2 font-medium text-slate-100">{i.client_name}</td>
                    <td className="px-2 py-2 font-mono-num text-xs text-slate-200">{i.invoice_number}</td>
                    <td className="px-2 py-2 text-xs text-slate-300">{formatDateID(i.event_date)}</td>
                    <td className="px-2 py-2 text-xs text-slate-400 truncate max-w-[140px]" title={i.venue}>{i.venue || '—'}</td>
                    <td className="px-2 py-2 text-right font-mono-num text-amber-200">{formatIDR(i.outstanding_amount)}</td>
                    <td className="px-2 py-2 text-xs text-slate-300">{formatDateID(i.due_date)}</td>
                    <td className="px-2 py-2 text-right text-xs text-rose-300 font-medium">{i.aging_days || 0}d</td>
                    <td className="px-2 py-2 text-xs text-slate-400">{i.last_follow_up_date ? formatDateID(i.last_follow_up_date) : 'Belum'}</td>
                    <td className="px-2 py-2"><StatusBadge status={i.status} /></td>
                    <td className="px-2 py-2 text-right">
                      <div className="inline-flex items-center gap-1">
                        <Button size="sm" variant="secondary" className="h-7 px-2 text-xs" onClick={() => navigate(`/followups?invoice=${i.id}`)} data-testid={`collection-fu-${i.id}`}>
                          <Phone className="h-3 w-3" />
                        </Button>
                        <Button size="sm" variant="secondary" className="h-7 px-2 text-xs" onClick={() => navigate(`/invoices/${i.id}`)} data-testid={`collection-detail-${i.id}`}>
                          Detail
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Mobile cards */}
          <div className="md:hidden space-y-2 p-3">
            {items.map(i => (
              <div key={i.id} className="rounded-lg border border-border bg-card/60 p-3" data-testid={`collection-card-${i.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate text-slate-100">{i.client_name}</div>
                    <div className="text-xs text-slate-300 font-mono-num">{i.invoice_number}</div>
                  </div>
                  <StatusBadge status={i.status} />
                </div>
                <div className="grid grid-cols-2 gap-2 mt-2 text-xs text-slate-300">
                  <div><span className="text-slate-400">Event:</span> {formatDateID(i.event_date)}</div>
                  <div className="truncate"><span className="text-slate-400">Venue:</span> {i.venue || '—'}</div>
                  <div><span className="text-slate-400">Due:</span> {formatDateID(i.due_date)}</div>
                  <div><span className="text-slate-400">Aging:</span> <span className="text-rose-300 font-medium">{i.aging_days || 0}d</span></div>
                </div>
                <div className="flex items-end justify-between gap-2 mt-3 pt-2 border-t border-border">
                  <div>
                    <div className="text-[10px] text-slate-400">Outstanding</div>
                    <div className="font-mono-num font-semibold text-amber-200">{formatIDR(i.outstanding_amount)}</div>
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="secondary" className="h-8 px-2" onClick={() => navigate(`/followups?invoice=${i.id}`)}>
                      <Phone className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="sm" className="h-8 px-2.5 text-xs" onClick={() => navigate(`/invoices/${i.id}`)}>Detail</Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </CardContent>
  </Card>
);

const DueSoonSection = ({ items, loading, navigate }) => (
  <Card className="bg-card border-border" data-testid="due-soon-card">
    <CardHeader className="pb-2">
      <CardTitle className="text-sm font-heading flex items-center gap-2 text-slate-100">
        <Clock className="h-4 w-4 text-amber-300" /> Invoice Jatuh Tempo 7 Hari
      </CardTitle>
    </CardHeader>
    <CardContent className="p-0 sm:p-4">
      {loading ? <Skeleton className="h-24 mx-4 sm:mx-0" /> :
       items.length === 0 ? <SectionEmpty>Tidak ada invoice yang jatuh tempo dalam 7 hari.</SectionEmpty> : (
        <>
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border">
                <tr>
                  <TH>Client</TH>
                  <TH>Invoice</TH>
                  <TH>Event</TH>
                  <TH>Venue</TH>
                  <TH>Due Date</TH>
                  <TH align="right">Outstanding</TH>
                  <TH>Status</TH>
                </tr>
              </thead>
              <tbody>
                {items.map(i => (
                  <tr key={i.id} className="border-b border-border/40 hover:bg-white/[0.03] cursor-pointer" onClick={() => navigate(`/invoices/${i.id}`)}>
                    <td className="px-2 py-2 font-medium text-slate-100">{i.client_name}</td>
                    <td className="px-2 py-2 font-mono-num text-xs text-slate-200">{i.invoice_number}</td>
                    <td className="px-2 py-2 text-xs text-slate-300">{formatDateID(i.event_date)}</td>
                    <td className="px-2 py-2 text-xs text-slate-400 truncate max-w-[140px]" title={i.venue}>{i.venue || '—'}</td>
                    <td className="px-2 py-2 text-xs text-slate-300">{formatDateID(i.due_date)}</td>
                    <td className="px-2 py-2 text-right font-mono-num text-amber-200">{formatIDR(i.outstanding_amount)}</td>
                    <td className="px-2 py-2"><StatusBadge status={i.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="md:hidden space-y-2 p-3">
            {items.map(i => (
              <div key={i.id} className="rounded-lg border border-border bg-card/60 p-3" onClick={() => navigate(`/invoices/${i.id}`)}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate text-slate-100">{i.client_name}</div>
                    <div className="text-xs text-slate-300 font-mono-num">{i.invoice_number}</div>
                  </div>
                  <StatusBadge status={i.status} />
                </div>
                <div className="grid grid-cols-2 gap-2 mt-2 text-xs text-slate-300">
                  <div><span className="text-slate-400">Event:</span> {formatDateID(i.event_date)}</div>
                  <div><span className="text-slate-400">Due:</span> {formatDateID(i.due_date)}</div>
                </div>
                <div className="font-mono-num font-semibold text-amber-200 mt-2">{formatIDR(i.outstanding_amount)}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </CardContent>
  </Card>
);

const QuotationFollowupSection = ({ items, loading, navigate }) => (
  <Card className="bg-card border-border" data-testid="quotation-followup-card">
    <CardHeader className="pb-2">
      <CardTitle className="text-sm font-heading flex items-center gap-2 text-slate-100">
        <MessageSquare className="h-4 w-4 text-primary" /> Penawaran Perlu Follow-Up
      </CardTitle>
    </CardHeader>
    <CardContent className="p-0 sm:p-4">
      {loading ? <Skeleton className="h-24 mx-4 sm:mx-0" /> :
       items.length === 0 ? <SectionEmpty>Tidak ada penawaran yang perlu follow-up.</SectionEmpty> : (
        <>
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border">
                <tr>
                  <TH>Client</TH>
                  <TH>No Penawaran</TH>
                  <TH>Event</TH>
                  <TH>Venue</TH>
                  <TH align="right">Total</TH>
                  <TH>Valid Until</TH>
                  <TH>Status</TH>
                </tr>
              </thead>
              <tbody>
                {items.map(q => (
                  <tr key={q.id} className="border-b border-border/40 hover:bg-white/[0.03] cursor-pointer" onClick={() => navigate(`/quotations/${q.id}`)}>
                    <td className="px-2 py-2 font-medium text-slate-100">{q.client_name}</td>
                    <td className="px-2 py-2 font-mono-num text-xs text-slate-200">{q.quotation_number}</td>
                    <td className="px-2 py-2 text-xs text-slate-300">{formatDateID(q.event_date)}</td>
                    <td className="px-2 py-2 text-xs text-slate-400 truncate max-w-[140px]" title={q.venue}>{q.venue || '—'}</td>
                    <td className="px-2 py-2 text-right font-mono-num text-slate-100">{formatIDR(q.total_amount)}</td>
                    <td className="px-2 py-2 text-xs text-slate-300">
                      {formatDateID(q.valid_until)}
                      {q.days_to_expire !== null && q.days_to_expire <= 3 && (
                        <span className="ml-1 text-[10px] text-amber-300 font-medium">({q.days_to_expire}d)</span>
                      )}
                    </td>
                    <td className="px-2 py-2"><StatusBadge status={q.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="md:hidden space-y-2 p-3">
            {items.map(q => (
              <div key={q.id} className="rounded-lg border border-border bg-card/60 p-3" onClick={() => navigate(`/quotations/${q.id}`)}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate text-slate-100">{q.client_name}</div>
                    <div className="text-xs text-slate-300 font-mono-num">{q.quotation_number}</div>
                  </div>
                  <StatusBadge status={q.status} />
                </div>
                <div className="grid grid-cols-2 gap-2 mt-2 text-xs text-slate-300">
                  <div><span className="text-slate-400">Event:</span> {formatDateID(q.event_date)}</div>
                  <div><span className="text-slate-400">Valid:</span> {formatDateID(q.valid_until)}</div>
                </div>
                <div className="font-mono-num font-semibold text-primary mt-2">{formatIDR(q.total_amount)}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </CardContent>
  </Card>
);

// ---------------------------------------------------------------------------
const TaxSummaryCard = ({ tax, loading }) => {
  const hasData = tax && (tax.invoices_with_ppn > 0 || tax.invoices_with_pph > 0 || tax.total_dpp > 0);
  return (
    <Card className="bg-card border-border" data-testid="tax-summary-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-heading flex items-center gap-2 text-slate-100">
          <Receipt className="h-4 w-4 text-primary" /> Ringkasan Pajak
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? <Skeleton className="h-32" /> : !hasData ? (
          <SectionEmpty>Belum ada data pajak pada periode ini.</SectionEmpty>
        ) : (
          <dl className="text-sm space-y-2">
            <Row label="Total DPP" value={formatIDR(tax.total_dpp)} />
            <Row label="Total DPP Nilai Lain" value={formatIDR(tax.total_dpp_nilai_lain)} muted />
            <Row label="Total PPN" value={formatIDR(tax.total_ppn)} accent="primary" />
            <Row label="Total PPh Dipotong" value={formatIDR(tax.total_pph_withheld)} accent="warn" />
            <Row label="Menunggu Bukti Potong" value={`${tax.invoices_waiting_bukti_potong} invoice`} muted />
          </dl>
        )}
      </CardContent>
    </Card>
  );
};

const Row = ({ label, value, muted, accent }) => {
  const cls = [
    'flex items-center justify-between gap-2',
    muted ? 'text-slate-400' : 'text-slate-200',
    accent === 'primary' ? 'text-primary' : '',
    accent === 'warn' ? 'text-amber-300' : '',
  ].filter(Boolean).join(' ');
  return <div className={cls}><dt>{label}</dt><dd className="font-mono-num">{value}</dd></div>;
};

// ---------------------------------------------------------------------------
const TopClientsTable = ({ clients, loading }) => (
  <Card className="bg-card border-border" data-testid="top-clients-card">
    <CardHeader className="pb-2">
      <CardTitle className="text-sm font-heading flex items-center gap-2 text-slate-100">
        <Eye className="h-4 w-4 text-primary" /> Top Client by Invoice
      </CardTitle>
    </CardHeader>
    <CardContent className="p-0 sm:p-4">
      {loading ? <Skeleton className="h-32 mx-4 sm:mx-0" /> : clients.length === 0 ? (
        <SectionEmpty>Belum ada data pada periode ini.</SectionEmpty>
      ) : (
        <>
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border">
                <tr>
                  <TH>Client</TH>
                  <TH align="right">Total Invoice</TH>
                  <TH align="right">Paid</TH>
                  <TH align="right">Outstanding</TH>
                  <TH align="right">Overdue</TH>
                  <TH align="right">Coll. Rate</TH>
                </tr>
              </thead>
              <tbody>
                {clients.map(c => (
                  <tr key={c.client_id} className="border-b border-border/40 hover:bg-white/[0.03]">
                    <td className="px-2 py-2 font-medium text-slate-100">{c.client_name}</td>
                    <td className="px-2 py-2 text-right font-mono-num text-slate-100">{formatIDR(c.total_invoice)}</td>
                    <td className="px-2 py-2 text-right font-mono-num text-emerald-300">{formatIDR(c.total_paid)}</td>
                    <td className="px-2 py-2 text-right font-mono-num text-amber-200">{formatIDR(c.outstanding)}</td>
                    <td className="px-2 py-2 text-right font-mono-num text-rose-300">{formatIDR(c.overdue)}</td>
                    <td className="px-2 py-2 text-right font-mono-num text-slate-100">{c.collection_rate.toFixed(0)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="md:hidden space-y-2 p-3">
            {clients.map(c => (
              <div key={c.client_id} className="rounded-lg border border-border bg-card/60 p-3">
                <div className="font-semibold truncate text-slate-100">{c.client_name}</div>
                <div className="grid grid-cols-2 gap-2 mt-2 text-xs text-slate-300">
                  <div><span className="text-slate-400">Invoice:</span> <span className="font-mono-num text-slate-100">{formatIDR(c.total_invoice)}</span></div>
                  <div><span className="text-slate-400">Paid:</span> <span className="font-mono-num text-emerald-300">{formatIDR(c.total_paid)}</span></div>
                  <div><span className="text-slate-400">Outstanding:</span> <span className="font-mono-num text-amber-200">{formatIDR(c.outstanding)}</span></div>
                  <div><span className="text-slate-400">Coll. Rate:</span> <span className="font-mono-num text-slate-100">{c.collection_rate.toFixed(0)}%</span></div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </CardContent>
  </Card>
);
